import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  CheckCircle2, XCircle, Search, ChevronLeft, ChevronRight,
  Loader2, BookOpen, Filter
} from "lucide-react";

const DIFF_STYLES = {
  easy: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700 border-amber-200" },
  hard: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-700 border-red-200" },
};

const QuestionPreviewDialog = ({ open, onClose, examId, examName }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAnswers, setShowAnswers] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (open && examId) {
      fetchQuestions();
    }
  }, [open, examId]);

  useEffect(() => { setPage(0); }, [filter, search]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exams/${examId}/questions-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const allQuestions = data
    ? [
        ...(filter === "all" || filter === "easy" ? data.questions.easy : []),
        ...(filter === "all" || filter === "medium" ? data.questions.medium : []),
        ...(filter === "all" || filter === "hard" ? data.questions.hard : []),
      ].filter(q =>
        !search || q.question_text.toLowerCase().includes(search.toLowerCase()) ||
        q.topic.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const totalPages = Math.ceil(allQuestions.length / PAGE_SIZE);
  const pageQuestions = allQuestions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm border-zinc-200 sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Question Bank — {examName || data?.exam_name}
          </DialogTitle>
          {data && (
            <p className="text-sm text-zinc-500 mt-1">
              {data.total_available} questions available · Exam selects {data.num_questions} per attempt
              (Easy: {data.difficulty_mix?.easy || 0}, Medium: {data.difficulty_mix?.medium || 0}, Hard: {data.difficulty_mix?.hard || 0})
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : data ? (
          <div className="flex flex-col gap-3 overflow-hidden flex-1">
            {/* Controls bar */}
            <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search questions or topics..."
                  className="pl-9 rounded-sm border-zinc-200 text-sm h-9"
                  data-testid="question-search"
                />
              </div>
              <div className="flex gap-1">
                {["all", "easy", "medium", "hard"].map(d => (
                  <Button
                    key={d}
                    size="sm"
                    variant={filter === d ? "default" : "outline"}
                    className={`rounded-sm h-9 text-xs capitalize ${filter === d ? "bg-zinc-900" : "border-zinc-200"}`}
                    onClick={() => setFilter(d)}
                    data-testid={`filter-${d}`}
                  >
                    {d === "all" ? `All (${(data.questions.easy?.length || 0) + (data.questions.medium?.length || 0) + (data.questions.hard?.length || 0)})` :
                     `${d} (${data.questions[d]?.length || 0})`}
                  </Button>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                className={`rounded-sm h-9 text-xs border-zinc-200 ${showAnswers ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : ''}`}
                onClick={() => setShowAnswers(!showAnswers)}
                data-testid="toggle-answers"
              >
                {showAnswers ? "Hide Answers" : "Show Answers"}
              </Button>
            </div>

            {/* Question cards */}
            <div className="overflow-y-auto flex-1 space-y-3 pr-1">
              {pageQuestions.length === 0 ? (
                <div className="text-center py-12 text-zinc-400 text-sm">No questions match your filter</div>
              ) : (
                pageQuestions.map((q, idx) => {
                  const s = DIFF_STYLES[q.difficulty] || DIFF_STYLES.medium;
                  const globalIdx = page * PAGE_SIZE + idx + 1;
                  return (
                    <div key={q.id} className={`${s.bg} border ${s.border} rounded-sm p-4`} data-testid={`question-card-${globalIdx}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full ${s.badge} flex items-center justify-center text-xs font-bold border`}>
                            {globalIdx}
                          </span>
                          <p className="text-sm font-medium text-zinc-800 leading-relaxed">{q.question_text}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Badge className={`${s.badge} border rounded-sm text-[10px] capitalize`}>{q.difficulty}</Badge>
                          <Badge variant="outline" className="border-zinc-200 rounded-sm text-[10px]">{q.topic}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-10">
                        {q.options.map((opt, oi) => {
                          const isCorrect = oi === q.correct_answer;
                          return (
                            <div
                              key={oi}
                              className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm border ${
                                showAnswers && isCorrect
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                                  : 'bg-white/70 border-zinc-200 text-zinc-700'
                              }`}
                              data-testid={`option-${globalIdx}-${oi}`}
                            >
                              {showAnswers && isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                              <span className="text-xs font-medium text-zinc-400 flex-shrink-0">{String.fromCharCode(65 + oi)}.</span>
                              <span>{opt}</span>
                            </div>
                          );
                        })}
                      </div>
                      {showAnswers && q.explanation && (
                        <p className="mt-2 ml-10 text-xs text-zinc-500 italic leading-relaxed">
                          {q.explanation}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2 border-t border-zinc-100 flex-shrink-0">
                <span className="text-xs text-zinc-500">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, allQuestions.length)} of {allQuestions.length}
                </span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 px-2 border-zinc-200"
                    onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="flex items-center px-3 text-xs text-zinc-600">{page + 1} / {totalPages}</span>
                  <Button size="sm" variant="outline" className="rounded-sm h-8 px-2 border-zinc-200"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-zinc-400 text-sm">Failed to load questions</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuestionPreviewDialog;
